import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { TipoProduto } from "@/types/tipo-produto";
import { formatCurrency } from "@/lib/utils";
import { format } from "date-fns";

interface TipoProdutoViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  produto: TipoProduto | null;
}

export function TipoProdutoViewDialog({
  open,
  onOpenChange,
  produto,
}: TipoProdutoViewDialogProps) {
  if (!produto) return null;

  const calcularMargem = (): string | null => {
    if (!produto.preco_custo || !produto.preco_venda || produto.preco_custo === 0) return null;
    const margem = ((produto.preco_venda - produto.preco_custo) / produto.preco_custo) * 100;
    return margem.toFixed(1);
  };

  const getMargemBadgeVariant = (margem: number): "default" | "secondary" | "destructive" => {
    if (margem >= 30) return "default";
    if (margem >= 10) return "secondary";
    return "destructive";
  };

  const margem = calcularMargem();
  const margemNum = margem ? parseFloat(margem) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Detalhes do Produto</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Nome</label>
              <p className="text-base">{produto.nome}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Categoria</label>
              <p className="text-base">{produto.categoria || "-"}</p>
            </div>
          </div>

          {produto.descricao && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Descrição</label>
              <p className="text-base">{produto.descricao}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Preço Base</label>
              <p className="text-base font-semibold">{formatCurrency(produto.preco_base)}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Unidade de Venda</label>
              <p className="text-base">{produto.unidade_venda}</p>
            </div>
          </div>

          {(produto.tamanho || produto.peso || produto.preco_custo || produto.preco_venda) && (
            <>
              <Separator className="my-4" />
              <div>
                <h3 className="text-base font-semibold mb-3">Informações de Beneficiamento</h3>
                <div className="space-y-3">
                  {produto.tamanho && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Tamanho</label>
                      <p className="text-base">{produto.tamanho}</p>
                    </div>
                  )}

                  {produto.peso && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Peso</label>
                      <p className="text-base">{produto.peso.toFixed(3)} kg</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    {produto.preco_custo && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Preço Custo</label>
                        <p className="text-base font-semibold">{formatCurrency(produto.preco_custo)}</p>
                      </div>
                    )}

                    {produto.preco_venda && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Preço Venda</label>
                        <p className="text-base font-semibold">{formatCurrency(produto.preco_venda)}</p>
                      </div>
                    )}
                  </div>

                  {margem && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Margem de Lucro</label>
                      <div className="mt-1">
                        <Badge variant={getMargemBadgeVariant(margemNum)} className="text-base">
                          {margem}%
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {produto.observacoes && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Observações</label>
              <p className="text-base">{produto.observacoes}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Criado em</label>
              <p className="text-sm">{format(new Date(produto.created_at), "dd/MM/yyyy HH:mm")}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Atualizado em</label>
              <p className="text-sm">{format(new Date(produto.updated_at), "dd/MM/yyyy HH:mm")}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
